import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

import type { LanguageFilterChipOption } from "../LanguageFilterChip";
import type { OfferLandingPages } from "./types";

import { useIsMobile } from "../hooks/useIsMobile";
import { GlobeIcon, ShareExternalIcon } from "../Icons";
import { LanguageFilterChip } from "../LanguageFilterChip";
import { Typo } from "../Typo";
import { pickDefaultLandingPage } from "./pickDefaultLandingPage";

type TargetPageCardTranslations = {
  languageLabel: string;
  languagesCount: (count: number) => string;
  previewLabel: string;
  noAvailableLanguagesLabel: string;
  clearFilterLabel: string;
};

type TargetPageCardProps = {
  offer: OfferLandingPages;
  isSelected: boolean;
  selectedLanguage: string;
  onSelect: () => void;
  onLanguageChange: (language: string) => void;
  translations: TargetPageCardTranslations;
  languageFilters: string[];
  onClearFilter?: () => void;
  getLanguageLabelFromCode: (code: string) => string;
};

export const TargetPageCard = ({
  offer,
  isSelected,
  selectedLanguage,
  onSelect,
  onLanguageChange,
  translations,
  languageFilters,
  onClearFilter,
  getLanguageLabelFromCode,
}: TargetPageCardProps) => {
  const pages = offer.landingPages ?? [];
  const isSingleLanguage = pages.length <= 1;

  const activeLanguage = isSelected
    ? (pages.find((p) => p.languageCode === selectedLanguage)?.languageCode ??
      pickDefaultLandingPage(offer)?.languageCode ??
      "en")
    : (pickDefaultLandingPage(offer)?.languageCode ?? "en");
  const activePage = pages.find((p) => p.languageCode === activeLanguage);
  const previewUrl = activePage?.previewUrl ?? "";

  const languageOptions: LanguageFilterChipOption[] = pages
    .map((p) => ({
      value: p.languageCode,
      label: getLanguageLabelFromCode(p.languageCode),
    }))
    .sort((a, b) => {
      if (a.value === "en" && b.value !== "en") return -1;
      if (a.value !== "en" && b.value === "en") return 1;
      return a.label.localeCompare(b.label);
    });

  const isMobile = useIsMobile();

  const isEmptyLanguageSelection =
    languageFilters.length > 0 &&
    languageFilters.reduce((acc, lang) => {
      return acc && !languageOptions.some((option) => option.value === lang);
    }, true);

  const isDisabled = isEmptyLanguageSelection;

  const containerClass = clsx(
    "relative flex cursor-pointer flex-col items-stretch gap-2 rounded-lg border bg-surface-primary p-4 text-left transition-colors",
    isSelected ? "border-2 border-stroke-focused" : "border-stroke-primary",
    isDisabled && "border-stroke-primary",
  );

  return (
    <div
      data-testid={"target-page-" + offer.id}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onClick={() => {
        if (isDisabled) return;
        onSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();

          if (isDisabled) return;
          onSelect();
        }
      }}
      className={containerClass}
      aria-disabled={isDisabled}
    >
      <div
        className={clsx(
          "flex flex-col gap-4",
          isDisabled && "pointer-events-none opacity-70",
        )}
        data-testid="target-page-main-content"
      >
        <div className="group relative flex h-[188px] items-center justify-center overflow-hidden rounded-lg bg-surface-secondary px-4 pt-4">
          {offer.thumbnailUrl ? (
            <img
              src={offer.thumbnailUrl}
              alt={activePage?.name ?? offer.name}
              className="h-full w-full object-contain object-top"
            />
          ) : (
            <div className="h-full w-full rounded bg-surface-tonal-default" />
          )}

          {previewUrl && isMobile ? (
            <div
              data-testid={`target-page-${offer.id}-preview`}
              className="absolute bottom-2 right-2"
            >
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={translations.previewLabel}
                onClick={(e) => e.stopPropagation()}
                className="flex h-8 w-8 items-center justify-center gap-1 rounded-lg bg-surface-inverse-primary"
              >
                <ShareExternalIcon
                  role="img"
                  className="h-5 w-5 text-text-inverse-primary"
                />
              </a>
            </div>
          ) : previewUrl && !isMobile ? (
            <div
              data-testid={`target-page-${offer.id}-preview`}
              className="absolute inset-0 hidden items-center justify-center bg-black/20 transition-opacity duration-300 ease-in-out group-hover:flex"
            >
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex h-8 items-center gap-1 rounded-lg bg-surface-inverse-primary px-3 py-2"
              >
                <ShareExternalIcon className="h-4 w-4 text-text-inverse-primary" />
                <Typo textColor="text-text-inverse-primary" type="bodySM">
                  {translations.previewLabel}
                </Typo>
              </a>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Typo
              type="labelSM"
              textColor={
                isDisabled ? "text-text-disabled" : "text-text-primary"
              }
              className="w-full truncate"
            >
              {activePage?.name ?? offer.name}
            </Typo>
            <Typo
              type="bodyXS"
              textColor={
                isDisabled ? "text-text-disabled" : "text-text-tertiary"
              }
              className="block w-full truncate"
            >
              {previewUrl || "No preview available"}
            </Typo>
          </div>
          <span
            className={clsx(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
              isSelected
                ? isDisabled
                  ? "bg-surface-disabled"
                  : "border-icon-primary bg-icon-primary"
                : "border-stroke-primary bg-surface-primary",
            )}
            aria-hidden="true"
          >
            {isSelected && !isDisabled && (
              <span className="h-3 w-3 rounded-full bg-surface-primary" />
            )}
            {isSelected && isDisabled && (
              <span className="h-3 w-3 rounded-full bg-stroke-secondary" />
            )}
          </span>
        </div>
      </div>
      <TargetPageFooter
        offerId={offer.id}
        isSelected={isSelected}
        isEmptyLanguageSelection={isEmptyLanguageSelection}
        isSingleLanguage={isSingleLanguage}
        activeLanguage={activeLanguage}
        languageOptions={languageOptions}
        onLanguageChange={onLanguageChange}
        onClearFilter={onClearFilter}
        translations={translations}
        getLanguageLabelFromCode={getLanguageLabelFromCode}
      />
    </div>
  );
};

const TargetPageFooter = ({
  offerId,
  isSelected,
  isEmptyLanguageSelection,
  isSingleLanguage,
  activeLanguage,
  languageOptions,
  onLanguageChange,
  onClearFilter,
  translations,
  getLanguageLabelFromCode,
}: {
  offerId: string;
  isSelected: boolean;
  isEmptyLanguageSelection: boolean;
  isSingleLanguage: boolean;
  activeLanguage: string | null;
  languageOptions: LanguageFilterChipOption[];
  onLanguageChange: (language: string) => void;
  onClearFilter?: () => void;
  translations: TargetPageCardTranslations;
  getLanguageLabelFromCode: (code: string) => string;
}) => {
  if (isSelected) {
    if (isEmptyLanguageSelection) {
      return (
        <div
          className="flex items-center justify-between rounded-full bg-surface-disabled px-4 py-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <div className="flex items-center gap-1">
            <GlobeIcon className="h-4 w-4 [&_path]:stroke-text-secondary" />
            <Typo type="bodyXS" textColor="text-text-secondary">
              {translations.noAvailableLanguagesLabel}
            </Typo>
          </div>

          <button type="button" onClick={onClearFilter}>
            <Typo
              type="bodyXS"
              textColor="text-text-secondary"
              className="underline"
            >
              {translations.clearFilterLabel}
            </Typo>
          </button>
        </div>
      );
    } else {
      return (
        <div
          className="flex items-center justify-between rounded-full bg-surface-brand-subtle px-4 py-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <div className="flex items-center gap-1">
            <GlobeIcon className="h-4 w-4 [&_path]:stroke-text-brand-default" />
            <Typo type="bodyXS" textColor="text-text-brand-default">
              {translations.languageLabel}
            </Typo>
          </div>
          {isSingleLanguage ? (
            <Typo type="bodyXS" textColor="text-text-brand-default">
              {activeLanguage ? getLanguageLabelFromCode(activeLanguage) : "-"}
            </Typo>
          ) : (
            <LanguageFilterChip
              mode="single"
              variant="trigger"
              options={languageOptions}
              translations={{
                label: translations.languageLabel,
              }}
              selectedValues={activeLanguage ? [activeLanguage] : []}
              onChange={(values: string[]) => onLanguageChange(values[0] ?? "")}
              data-testid={`target-page-${offerId}-language`}
              align="right"
              modal
            />
          )}
        </div>
      );
    }
  } else {
    return (
      <span className="mt-2 self-start">
        <LanguagesTooltip
          id={`target-page-${offerId}-languages-tooltip`}
          langCodes={languageOptions.map((option) => option.value)}
          translations={translations}
          getLanguageLabelFromCode={getLanguageLabelFromCode}
        />
      </span>
    );
  }
};

const LanguagesTooltip = ({
  id,
  langCodes,
  translations,
  getLanguageLabelFromCode,
}: {
  id: string;
  langCodes: string[];
  translations: TargetPageCardTranslations;
  getLanguageLabelFromCode: (code: string) => string;
}) => {
  const isMobile = useIsMobile();
  const isSingle = langCodes.length === 1;
  const useTwoColumns = langCodes.length > 4;

  const trigger = (
    <div
      data-testid="ui-tooltip"
      className="relative inline-block cursor-pointer"
      data-tooltip-id={isMobile ? undefined : id}
    >
      <div className="flex items-center gap-1">
        <GlobeIcon className="h-4 w-4 [&_path]:stroke-icon-secondary" />
        {isSingle ? (
          <Typo type="bodyXS" textColor="text-text-secondary">
            {getLanguageLabelFromCode(langCodes[0])}
          </Typo>
        ) : (
          <Typo
            type="bodyXS"
            textColor="text-text-secondary"
            className="underline hover:no-underline"
          >
            {translations.languagesCount(langCodes.length)}
          </Typo>
        )}
      </div>
    </div>
  );

  const languagesList =
    langCodes.length === 0 ? (
      translations.languageLabel
    ) : (
      <div
        className={clsx(
          "max-h-[160px] overflow-y-auto pr-1",
          useTwoColumns
            ? "columns-2 gap-3 [column-fill:auto]"
            : "flex flex-col gap-2",
        )}
      >
        {langCodes.map((code) => (
          <Typo
            key={code}
            type="bodySM"
            className={clsx(useTwoColumns && "mb-2 break-inside-avoid")}
          >
            {getLanguageLabelFromCode(code)}
          </Typo>
        ))}
      </div>
    );

  if (isMobile) {
    if (isSingle) {
      return trigger;
    }
    return (
      <MobileLanguagesPopover trigger={trigger}>
        {languagesList}
      </MobileLanguagesPopover>
    );
  }

  return (
    <>
      {trigger}
      <ReactTooltip
        id={id}
        place="bottom-start"
        className="z-50 !h-auto !w-[256px] !rounded-lg !bg-surface-primary !p-4 text-left !opacity-100 shadow-lg shadow-stroke-primary !transition-opacity"
        opacity={1}
        delayShow={200}
        delayHide={0}
        noArrow
        disableTooltip={() => isSingle}
      >
        {languagesList}
      </ReactTooltip>
    </>
  );
};

const MobileLanguagesPopover = ({
  trigger,
  children,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="bg-transparent p-0"
      >
        {trigger}
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute left-0 top-full z-50 mt-2 w-[256px] rounded-lg bg-surface-primary p-4 shadow-lg shadow-stroke-primary"
        >
          {children}
        </div>
      )}
    </div>
  );
};
